import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Outer wrapper holds a 2px padding band that callers paint via
 * `className` (typically a gradient). Inner fill takes the available
 * space and renders the children on a dark background.
 *
 * Radius math: outer `rounded-2xl` = 18px in our token scale. With 2px
 * padding the inner radius must be 16px to keep the gradient band an
 * even thickness around the curve (outer − padding = inner).
 */
const flowHubVariants = cva(
  "relative inline-flex shrink-0 items-center justify-center rounded-2xl p-[2px] [&_svg]:shrink-0",
  {
    variants: {
      size: {
        sm: "size-12 [&_svg]:size-5",
        md: "size-16 [&_svg]:size-7",
        lg: "size-20 [&_svg]:size-9",
        xl: "size-28 [&_svg]:size-12",
      },
    },
    defaultVariants: {
      size: "lg",
    },
  }
);

type FlowHubProps = {
  /**
   * Center content (logo, icon, glyph). Rendered inside a dark fill —
   * children should use `text-background` or similar to read clearly.
   */
  children: ReactNode;
} & ComponentProps<"span"> &
  VariantProps<typeof flowHubVariants>;

function FlowHub({ children, className, size, ...props }: FlowHubProps) {
  return (
    <span
      className={cn(flowHubVariants({ size, className }))}
      data-slot="flow-hub"
      {...props}
    >
      <span className="flex size-full items-center justify-center rounded-[16px] bg-foreground text-background">
        {children}
      </span>
    </span>
  );
}

export { FlowHub, flowHubVariants };
export type { FlowHubProps };
